import { useState, useEffect } from 'react';
import { useItems } from '../hooks/useItems';
import { useCategories } from '../hooks/useCategories';
import PropTypes from 'prop-types';

const ItemModal = ({ show, onHide, onSave, item, categories }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    category_id: item?.category_id || '',
    unit_of_measure: item?.unit_of_measure || 'each',
    default_price: item?.default_price || ''
  });

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category_id: item.category_id || '',
        unit_of_measure: item.unit_of_measure || 'each',
        default_price: item.default_price || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category_id: '',
        unit_of_measure: 'each',
        default_price: ''
      });
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, category_id: parseInt(formData.category_id) });
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{item ? 'Edit Item' : 'New Item'}</h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Unit of Measure *</label>
                <select
                  className="form-select"
                  value={formData.unit_of_measure}
                  onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                  required
                >
                  <option value="each">Each</option>
                  <option value="box">Box</option>
                  <option value="lb">Pound (lb)</option>
                  <option value="ft">Foot (ft)</option>
                  <option value="yd">Yard (yd)</option>
                  <option value="gal">Gallon (gal)</option>
                  <option value="dozen">Dozen</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Default Price
                  <small className="text-muted ms-2">(Used when adding new inventory)</small>
                </label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    className="form-control"
                    step="0.01"
                    min="0"
                    value={formData.default_price || ''}
                    onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

ItemModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  item: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    category_id: PropTypes.number,
    unit_of_measure: PropTypes.string
  }),
  categories: PropTypes.arrayOf(PropTypes.object).isRequired
};

const ItemsPage = () => {
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [showZeroPriceOnly, setShowZeroPriceOnly] = useState(false);
  const { items: rawItems, total, loading, error, createItem, updateItem, deleteItem } = useItems(categoryFilter, page, limit, searchTerm);
  const { categories } = useCategories();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Apply client-side filter for zero price
  const items = showZeroPriceOnly 
    ? rawItems.filter(item => parseFloat(item.default_price) === 0 || !item.default_price)
    : rawItems;

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateItem(editingItem.id, formData);
      } else {
        await createItem(formData);
      }
      setShowModal(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Failed to save item:', err);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteItem(itemId);
      } catch (err) {
        console.error('Failed to delete item:', err);
      }
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Inventory Items</h2>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingItem(null);
            setShowModal(true);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          New Item
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={categoryFilter || ''}
                onChange={(e) => setCategoryFilter(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showZeroPrice"
                  checked={showZeroPriceOnly}
                  onChange={(e) => setShowZeroPriceOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showZeroPrice">
                  Show only $0 default price
                </label>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>Default Price</th>
                    <th>Description</th>
                    <th width="150">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No items found
                      </td>
                    </tr>
                  ) : (
                    items.map(item => (
                      <tr key={item.id}>
                        <td><strong>{item.name}</strong></td>
                        <td>
                          <span className="badge bg-info">{item.category?.name}</span>
                        </td>
                        <td>{item.unit_of_measure}</td>
                        <td>
                          {item.default_price ? (
                            <span className="text-success">${parseFloat(item.default_price).toFixed(2)}</span>
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                        </td>
                        <td>{item.description || <span className="text-muted">No description</span>}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(item.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} items
              </div>
              <div>
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ItemModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        item={editingItem}
        categories={categories}
      />
    </div>
  );
};

export default ItemsPage;
